import WebBluetoothClient from './web_bluetooth_client';
import WebBluetoothEvents from './web_bluetooth_events';
import { bthomeDeviceToModel } from '../virtual_helpers/virtual_bthome_helper';

/**
 * BLE client for BLU‑series devices.
 * Handles provisioning (JWT) that may be split across LOW & HIGH
 * characteristics and fetches device‑settings exposure.
 */
export default class WebBluetoothBleClient extends WebBluetoothClient {
	public static readonly DEVICE_SERVICE_UUID = 'de8a5aac-a99b-c315-0c80-60d4cbb51225';
	private readonly UUID_PROVISION_DATA_LO = '0c31d671-5262-4620-87f3-51851c367cec';
	private readonly UUID_PROVISION_DATA_HI = '55bef871-f611-46f5-b498-7b9cc9759927';
	private readonly WRITE_PASS_KEY_UUID = '0ffb7104-860c-49ae-8989-1f946d5f6c03';
	private readonly ENCRYPTION_KEY_UUID = 'eb0fb41b-af4b-4724-a6f9-974f55aba81a';
	private readonly BT_HOME_UUID = 'eb0fb41b-af4b-4724-a6f9-974f55aba81a';
	private readonly SETTINGS_UUID = {
		beacon_mode: 'cb9e957e-952d-4761-a7e1-4416494a5bfa',
		buzzer: 'dd78bf35-7680-484e-ad86-1bc1e7738e14',
		angle_report_threshold: '86e7cc43-19f4-4f38-b5ad-1ae586237e2a',
	} as const;

	constructor(connectedDevice: BluetoothDevice) {
		super(connectedDevice);
	}

	public get isBLUDevice(): boolean {
		return true;
	}

	protected async initService() {
		try {
			const service: BluetoothRemoteGATTService = await this.server.getPrimaryService(
				WebBluetoothBleClient.DEVICE_SERVICE_UUID,
			);
			const loChar: BluetoothRemoteGATTCharacteristic = await service.getCharacteristic(
				this.UUID_PROVISION_DATA_LO,
			);
			const decoder = new TextDecoder('utf-8');
			const loValue = await loChar.readValue();
			const jwtLo = decoder.decode(loValue);

			let jwtFull: string = jwtLo;
			try {
				const hiChar = await service.getCharacteristic(this.UUID_PROVISION_DATA_HI);
				const hiValue = await hiChar.readValue();
				const jwtHi = decoder.decode(hiValue);
				jwtFull = (jwtLo + jwtHi).replace(/\0+$/g, '');
			} catch (error) {
				// HIGH chr not present – older FW; LOW already contains full token
				console.log(error);
			}

			const connData = this._decodeJwt(jwtFull);
			const characteristics = await service.getCharacteristics();
			const settings = this.SETTINGS_UUID;
			const methods: string[] = [];

			characteristics.forEach(async (i: BluetoothRemoteGATTCharacteristic) => {
				const method = Object.keys(settings).find((key) => settings[key] === i.uuid);
				if (i.properties.read && method) methods.push(method);
			});
			if (methods.includes('beacon_mode')) {
				connData.beacon_mode = await this._readCharacteristic(service, settings.beacon_mode);
			}
			if (methods.includes('buzzer')) {
				connData.buzzer = await this._readCharacteristic(service, settings.buzzer);
			}
			// connData.encryption_key = await this._readEncryptionKey(service);
			this._dispatchEvent(WebBluetoothEvents.CONNECTED, connData);
		} catch (error) {
			this._onError({
				type: WebBluetoothEvents.Errors.SERVICE_ERROR,
				message: error?.message || error,
			});
		}
	}
	private async _readCharacteristic(
		service: BluetoothRemoteGATTService,
		uuid: string,
	): Promise<boolean> {
		let value = false;
		try {
			const characteristic: BluetoothRemoteGATTCharacteristic =
				await service.getCharacteristic(uuid);
			const readValue: DataView = await characteristic.readValue();
			value = readValue.getUint8(0) == 0x01;
		} catch (error) {
			console.log(error);
		} finally {
			return value;
		}
	}
	private async _readEncryptionKey(service: BluetoothRemoteGATTService): Promise<string> {
		let hexKey = '';
		try {
			const passkeyCharacteristic = await service.getCharacteristic(this.WRITE_PASS_KEY_UUID);
			const passkeyString = 'qwerty';
			const passkey = new TextEncoder().encode(passkeyString);
			console.log('Passkey', { passkey });
			await passkeyCharacteristic.writeValue(passkey);
			console.log('Passkey set');

			const encryptionCharacteristic: BluetoothRemoteGATTCharacteristic =
				await service.getCharacteristic(this.ENCRYPTION_KEY_UUID);
			const value = await encryptionCharacteristic.readValue();
			const encryptionKey = new Uint8Array(value.buffer);
			console.log('Encryption Key:', encryptionKey);

			hexKey = Array.from(encryptionKey)
				.map((byte) => byte.toString(16).padStart(2, '0'))
				.join('');
			console.log('Encryption Key (Hex):', hexKey);
		} catch (error) {
			console.log(error);
		} finally {
			return hexKey;
		}
	}
	private _resolveJwtModel = (decodedPayload: Record<string, unknown>): string => {
		const modelId =
			typeof decodedPayload.mid === 'number'
				? decodedPayload.mid
				: typeof decodedPayload.model_id === 'number'
					? decodedPayload.model_id
					: undefined;

		if (modelId !== undefined) {
			const matchedModel = bthomeDeviceToModel.find((entry) => entry.id === modelId);
			if (matchedModel?.type) {
				return matchedModel.type;
			}
		}

		const rawType = decodedPayload.m;
		if (typeof rawType === 'number') {
			const matchedModel = bthomeDeviceToModel.find((entry) => entry.id === rawType);
			if (matchedModel?.type) {
				return matchedModel.type;
			}
		}

		return typeof rawType === 'string' ? rawType : '';
	};

	private _decodeJwt(token: string): {
		id: string;
		type: string;
		beacon_mode: boolean;
		buzzer: boolean;
		encryption_key: string;
	} {
		const [_header, payload, _signature] = token.split('.');
		const decodedPayload = JSON.parse(this._base64UrlDecode(payload));
		return {
			id: decodedPayload.mac,
			type: this._resolveJwtModel(decodedPayload),
			beacon_mode: false,
			buzzer: false,
			encryption_key: '',
		};
	}
	private _base64UrlDecode(str: string): string {
		let output = str.replace(/-/g, '+').replace(/_/g, '/');
		switch (output.length % 4) {
			case 0:
				break;
			case 2:
				output += '==';
				break;
			case 3:
				output += '=';
				break;
			default:
				throw new Error('Invalid base64 string');
		}
		return atob(output);
	}
}
